import React, { useState, useMemo, useEffect } from 'react';
import { QUESTIONS_DATA } from '../data/questions';
import { Button } from './Button';
import { ArrowLeft, ArrowRight, Info, CheckCircle, XCircle, Camera, Mail, Upload, Phone } from 'lucide-react';

interface QuestionnaireProps {
    initialEmail?: string;
    onClose: () => void;
    forceSuccess?: boolean;
}

type ViewState = 'questions' | 'result' | 'lead' | 'success';
type AmpelStatus = 'green' | 'yellow' | 'red';

export const Questionnaire: React.FC<QuestionnaireProps> = ({ initialEmail = '', onClose, forceSuccess = false }) => {
    const [view, setView] = useState<ViewState>(forceSuccess ? 'success' : 'questions');
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<number, boolean>>({});
    const [infoVisible, setInfoVisible] = useState(false);

    useEffect(() => {
        if (forceSuccess) setView('success');
    }, [forceSuccess]);

    useEffect(() => {
        if (view === 'lead') {
            const script = document.createElement('script');
            script.src = '//js-eu1.hsforms.net/forms/embed/v2.js';
            script.charset = 'utf-8';
            script.type = 'text/javascript';
            script.async = true;

            script.onload = () => {
                // @ts-ignore
                if (window.hbspt) {
                    // @ts-ignore
                    window.hbspt.forms.create({
                        portalId: "147631815",
                        formId: "1b5a86e5-7e1c-4d1b-82a1-1d448e03a2f9",
                        region: "eu1",
                        target: "#hubspot-questionnaire-form",
                        onFormSubmitted: () => {
                            setView('success');
                        }
                    });
                }
            };

            document.body.appendChild(script);

            return () => {
                const hsScript = document.querySelector('script[src="//js-eu1.hsforms.net/forms/embed/v2.js"]');
                if (hsScript) {
                    document.body.removeChild(hsScript);
                }
            };
        }
    }, [view]);

    const allQuestions = useMemo(() => {
        return QUESTIONS_DATA.sections.flatMap(section => section.questions);
    }, []);

    const currentQuestion = allQuestions[currentQuestionIndex];
    
    const currentSection = QUESTIONS_DATA.sections.find(s => 
        s.questions.some(q => q.id === currentQuestion.id)
    );

    const progress = ((currentQuestionIndex + 1) / allQuestions.length) * 100;

    const handleAnswer = (answer: boolean) => {
        setAnswers(prev => ({ ...prev, [currentQuestion.id]: answer }));
    };

    const handleNext = () => {
        if (currentQuestionIndex < allQuestions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
            setInfoVisible(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setView('result');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
            setInfoVisible(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            if(window.confirm("Analyse abbrechen?")) {
                onClose();
            }
        }
    };

    const result = useMemo(() => {
        let negativeAnswers = 0;
        Object.values(answers).forEach(val => {
            if (val === false) negativeAnswers++;
        });

        const { scoring } = QUESTIONS_DATA;
        if (negativeAnswers >= scoring.red.min) return { status: 'red' as AmpelStatus, data: scoring.red };
        if (negativeAnswers >= scoring.yellow.min) return { status: 'yellow' as AmpelStatus, data: scoring.yellow };
        return { status: 'green' as AmpelStatus, data: scoring.green };
    }, [answers]);

    const renderQuestions = () => (
        <div className="max-w-2xl mx-auto w-full flex flex-col h-full pt-4">
            <div className="mb-6">
                <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                    <div 
                        className="bg-accent h-1.5 rounded-full transition-all duration-500 ease-out shadow-sm" 
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
                <div className="flex justify-between text-[9px] md:text-xs text-gray-400 font-black tracking-widest uppercase">
                    <span>FRAGE {currentQuestionIndex + 1} / {allQuestions.length}</span>
                    <span className="text-accent">{currentSection?.title.split(':')[0]}</span>
                </div>
            </div>

            <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-10 border border-gray-200 shadow-xl mb-6 min-h-[260px] md:min-h-[340px] flex flex-col relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1.5 h-full bg-accent/20"></div>
                <h3 className="text-xl md:text-2xl font-black text-primary mb-5 md:mb-8 leading-tight">
                    {currentQuestion.text}
                </h3>
                
                <button 
                    onClick={() => setInfoVisible(!infoVisible)}
                    className="inline-flex items-center gap-2 text-xs md:text-sm text-accent font-black mb-6 hover:underline focus:outline-none w-fit uppercase tracking-widest"
                >
                    <Info className="w-4 h-4" />
                    {infoVisible ? 'Schliessen' : 'Experten-Erläuterung'}
                </button>

                {infoVisible && (
                    <div className="bg-surface border-l-4 border-accent p-4 rounded-r-lg text-xs md:text-sm text-gray-600 mb-8 animate-in fade-in slide-in-from-top-1 duration-300 leading-relaxed shadow-inner">
                        {currentQuestion.info}
                    </div>
                )}

                <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button 
                      onClick={() => handleAnswer(true)} 
                      className={`py-4 md:py-5 rounded-xl border-2 transition-all font-black text-base md:text-lg uppercase tracking-wide active:scale-[0.98] ${answers[currentQuestion.id] === true ? 'border-accent bg-accent text-white shadow-lg' : 'border-gray-100 bg-white text-gray-400'}`}
                    >
                      Ja
                    </button>
                    <button 
                      onClick={() => handleAnswer(false)} 
                      className={`py-4 md:py-5 rounded-xl border-2 transition-all font-black text-base md:text-lg uppercase tracking-wide active:scale-[0.98] ${answers[currentQuestion.id] === false ? 'border-accent bg-accent text-white shadow-lg' : 'border-gray-100 bg-white text-gray-400'}`}
                    >
                      Nein
                    </button>
                </div>
            </div>

            <div className="flex justify-between items-center mt-auto pb-8">
                <button onClick={handleBack} className="text-gray-400 hover:text-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 px-2"><ArrowLeft className="w-4 h-4" /> Zurück</button>
                <Button onClick={handleNext} disabled={answers[currentQuestion.id] === undefined} className="shadow-xl py-4 px-8 text-sm md:text-base font-black uppercase tracking-widest">
                    {currentQuestionIndex === allQuestions.length - 1 ? 'Auswertung' : 'Nächste Frage'} <ArrowRight className="w-4 h-4 ml-2 inline" />
                </Button>
            </div>
        </div>
    );

    const renderResult = () => (
        <div className="max-w-2xl mx-auto w-full text-center px-2 py-4">
            <div className="mb-6 flex justify-center">
                <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center shadow-2xl animate-in zoom-in duration-500 ${result.status === 'red' ? 'bg-red-500' : result.status === 'yellow' ? 'bg-yellow-400' : 'bg-green-500'} text-white`}>
                     <span className="text-2xl md:text-3xl font-black tracking-tighter">{result.data.label}</span>
                </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-primary mb-4 leading-tight">{result.data.title}</h2>
            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-200 text-left mb-8 shadow-sm">
                <p className="text-sm md:text-lg text-gray-600 mb-6 leading-relaxed">{result.data.description}</p>
                <div className={`p-4 md:p-5 rounded-lg bg-surface border-l-4 ${result.status === 'red' ? 'border-red-500' : result.status === 'yellow' ? 'border-yellow-400' : 'border-green-500'}`}>
                    <strong className="block mb-2 text-primary text-base md:text-lg uppercase tracking-wide font-black">Empfehlung:</strong>
                    <p className="text-gray-700 leading-relaxed font-medium text-sm md:text-base italic">{result.data.recommendation}</p>
                </div>
            </div>
            <Button onClick={() => setView('lead')} fullWidth className="text-base md:text-lg py-5 shadow-2xl font-black uppercase tracking-widest active:scale-95 transition-transform">Ergebnis & Leitfaden anfordern</Button>
        </div>
    );

    const renderLeadForm = () => (
        <div className="max-w-xl mx-auto w-full px-2 py-4">
            <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-black text-primary mb-2 leading-tight">Fast geschafft!</h2>
                <p className="text-gray-500 text-xs md:text-base font-bold uppercase tracking-widest">E-Mail für den ICT-Risiko-Leitfaden:</p>
            </div>

            <div className="bg-white p-6 md:p-12 rounded-xl md:rounded-2xl border border-gray-200 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-accent"></div>
                <div id="hubspot-questionnaire-form" className="hubspot-form-wrapper">
                    <div className="flex flex-col items-center justify-center py-10 text-accent/30 animate-pulse">
                        <div className="w-8 h-8 border-2 border-accent/20 border-t-accent rounded-full animate-spin mb-4"></div>
                        <p className="text-[10px] font-black uppercase tracking-widest">Formular lädt...</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSuccess = () => (
        <div className="max-w-2xl mx-auto w-full px-2 py-8">
            <div className="bg-white rounded-xl md:rounded-2xl p-8 md:p-12 border border-gray-200 shadow-2xl text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce"><CheckCircle className="w-8 h-8 text-green-600" /></div>
                <h2 className="text-2xl md:text-4xl font-black text-primary mb-4">Vielen Dank!</h2>
                <p className="text-base md:text-xl text-green-600 font-black mb-4 uppercase tracking-tighter">Ihre Unterlagen sind unterwegs.</p>
                <div className="mt-10 pt-8 border-t border-divider text-left">
                    <h3 className="text-base md:text-lg font-black text-primary mb-3 uppercase tracking-wide">Nächste Schritte</h3>
                    <p className="text-gray-600 text-xs md:text-base leading-relaxed mb-4">Markus Renggli prüft Ihre Angaben und meldet sich bei Rückfragen innerhalb von 48h bei Ihnen.</p>
                </div>
            </div>
            <div className="mt-8 text-center"><button onClick={onClose} className="text-gray-400 font-black text-xs uppercase tracking-widest hover:text-accent transition-colors">Zurück zur Startseite</button></div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 bg-surface overflow-y-auto">
            <div className="min-h-screen flex flex-col">
                <header className="bg-white border-b border-gray-200 px-5 py-4 sticky top-0 z-10 shadow-sm flex items-center justify-between">
                    <img src="https://raw.githubusercontent.com/atedo-ch/rombus/main/rombus-logo.png" alt="rombus logo" className="h-6 md:h-8 w-auto" />
                    <button onClick={onClose} className="p-2 active:bg-gray-100 rounded-full transition-colors"><XCircle className="w-6 h-6 text-gray-400" /></button>
                </header>
                <main className="flex-grow flex flex-col items-center justify-start md:justify-center p-5 md:p-8">
                    {view === 'questions' && renderQuestions()}
                    {view === 'result' && renderResult()}
                    {view === 'lead' && renderLeadForm()}
                    {view === 'success' && renderSuccess()}
                </main>
            </div>
        </div>
    );
};